/**
 * Code viewer pane for the plugin builder.
 * Displays a file tree and read-only code viewer showing generated files.
 */

import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { File, Folder, FolderOpen, ChevronRight, ChevronDown } from 'lucide-react';
import type { VirtualFileSystem } from '../types';

interface CodeViewerPaneProps {
  files: VirtualFileSystem | null;
  className?: string;
}

interface TreeNode {
  name: string;
  path: string;
  isDir: boolean;
  children: TreeNode[];
}

function buildFileTree(files: VirtualFileSystem): TreeNode[] {
  const root: TreeNode[] = [];

  for (const filePath of Object.keys(files).sort()) {
    const parts = filePath.split('/');
    let current = root;

    for (let i = 0; i < parts.length; i++) {
      const name = parts[i];
      const path = parts.slice(0, i + 1).join('/');
      const isDir = i < parts.length - 1;

      let existing = current.find((n) => n.name === name && n.isDir === isDir);
      if (!existing) {
        existing = { name, path, isDir, children: [] };
        current.push(existing);
      }
      current = existing.children;
    }
  }

  return root;
}

function FileTreeItem({
  node,
  depth,
  selectedPath,
  onSelect,
}: {
  node: TreeNode;
  depth: number;
  selectedPath: string | null;
  onSelect: (path: string) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const isSelected = selectedPath === node.path;

  if (node.isDir) {
    return (
      <div>
        <button
          className={cn(
            'flex items-center gap-1 w-full text-left text-sm py-1 px-2 hover:bg-muted/50 rounded transition-colors',
          )}
          style={{ paddingLeft: `${depth * 12 + 8}px` }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          )}
          {isExpanded ? (
            <FolderOpen className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
          ) : (
            <Folder className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
          )}
          <span className="truncate">{node.name}</span>
        </button>
        {isExpanded &&
          node.children.map((child) => (
            <FileTreeItem
              key={child.path}
              node={child}
              depth={depth + 1}
              selectedPath={selectedPath}
              onSelect={onSelect}
            />
          ))}
      </div>
    );
  }

  return (
    <button
      className={cn(
        'flex items-center gap-1 w-full text-left text-sm py-1 px-2 rounded transition-colors',
        isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50',
      )}
      style={{ paddingLeft: `${depth * 12 + 8}px` }}
      onClick={() => onSelect(node.path)}
    >
      <span className="w-3.5 flex-shrink-0" />
      <File className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
      <span className="truncate">{node.name}</span>
    </button>
  );
}

export function CodeViewerPane({ files, className }: CodeViewerPaneProps) {
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  const tree = useMemo(() => (files ? buildFileTree(files) : []), [files]);

  // Auto-select first file if none selected
  const fileKeys = files ? Object.keys(files).sort() : [];
  const effectiveSelected =
    selectedFile && files?.[selectedFile] ? selectedFile : fileKeys[0] || null;

  const displayContent = effectiveSelected && files?.[effectiveSelected]?.content;
  const displayLanguage = effectiveSelected && files?.[effectiveSelected]?.language;

  if (!files || fileKeys.length === 0) {
    return (
      <div
        className={cn('flex items-center justify-center text-muted-foreground text-sm', className)}
      >
        No files generated yet
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* File tree */}
      <div className="border-b overflow-y-auto max-h-[200px] py-1">
        {tree.map((node) => (
          <FileTreeItem
            key={node.path}
            node={node}
            depth={0}
            selectedPath={effectiveSelected}
            onSelect={setSelectedFile}
          />
        ))}
      </div>

      {/* Code content */}
      <div className="flex-1 overflow-auto">
        {displayContent ? (
          <div className="relative">
            <div className="absolute top-2 right-2">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                {displayLanguage}
              </span>
            </div>
            <pre className="p-4 text-sm font-mono whitespace-pre-wrap break-words">
              <code>{displayContent}</code>
            </pre>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            Select a file to view its contents
          </div>
        )}
      </div>
    </div>
  );
}
